/**
 * Network protocol types for Poker Reborn
 * Based on GameLink (GL) protocol from legacy AS3 clients
 */

import type {
  ActionType,
  GameInfo,
  PlayerProfile,
  TableSize,
} from './game';

// =============================================================================
// GL PACKET STRUCTURE
// =============================================================================

/** GL packet types */
export type PacketType = 'A' | 'O' | 'P' | 'S' | 'E';

/** Base GL packet structure */
export interface GLPacket {
  from: string;
  to: string;
  type: PacketType;
  body: string;
}

/** Packet type meanings */
export const PacketTypes = {
  AUTH: 'A' as const,       // Authentication request
  AUTH_OK: 'O' as const,    // Authentication success
  PAYLOAD: 'P' as const,    // Game messages (JSON)
  SERVICE: 'S' as const,    // Service notifications
  ERROR: 'E' as const,      // Error messages
};

// =============================================================================
// CONNECTION
// =============================================================================

/** Connection states */
export type ConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'authenticating'
  | 'connected'
  | 'reconnecting';

/** Close codes */
export const CloseCodes = {
  NORMAL: 1000,
  AUTH_TIMEOUT: 4001,
  IDLE_TIMEOUT: 4002,
  SECOND_AUTH: 4003,
  BAD_AUTH: 4004,
  PROTOCOL_ERROR: 4005,
} as const;

/** Service messages from server */
export type ServiceMessage = 'soft_timeout' | 'hard_timeout' | 'second_auth';

/** Error messages from server */
export type ErrorMessage = 'not_found' | 'not_auth' | 'bad_auth';

// =============================================================================
// SERVER MESSAGES (Incoming)
// =============================================================================

/** Base server message */
export interface ServerMessage {
  a: string;
  [key: string]: unknown;
}

/** Profile response */
export interface ProfileMessage extends ServerMessage {
  a: 'profile';
  profile: PlayerProfile;
  chips: number;
  level: number;
  xp: number;
}

/** Games list response */
export interface GamesMessage extends ServerMessage {
  a: 'games';
  managers: Array<{
    id: string;
    name: string;
    type: 'cash' | 'sng' | 'mtt' | 'fastfold';
    tables: Array<{
      id: string;
      name: string;
      seats: TableSize;
      sb: number;
      bb: number;
      players: number;
      avgPot?: number;
    }>;
  }>;
}

/** Table state snapshot */
export interface StateMessage extends ServerMessage {
  a: 'state' | 'state2';
  tableId: string;
  gameInfo: {
    numSits: TableSize;
    wait: number;
    bb: number;
    sb: number;
    minBuyin: number;
    maxBuyin: number;
  };
  players: Array<{
    sn: number;
    pos: string;
    status: string;
    br: number;
    player: {
      id: string;
      profile: PlayerProfile;
    };
  }>;
}

/** Hand start */
export interface StartMessage extends ServerMessage {
  a: 'start' | 'start2';
  gn: number;
  gameInfo: GameInfo;
  players: Array<{
    sn: number;
    pos: string;
    status: string;
    br: number;
    player: {
      id: string;
      profile: PlayerProfile;
    };
  }>;
}

/** Blinds posted */
export interface BlindsMessage extends ServerMessage {
  a: 'blinds';
  sb: { player: { id: string }; amount: number };
  bb: { player: { id: string }; amount: number };
  ante?: Array<{ player: { id: string }; amount: number }>;
}

/** Hole cards dealt */
export interface CardMessage extends ServerMessage {
  a: 'card';
  player: { id: string };
  cards: [string, string];
}

/** Action on player */
export interface ActMessage extends ServerMessage {
  a: 'act';
  player: { id: string };
  actions: ActionType[];
  amount: number;
  minRaise: number;
  br: number;
  timeout: number;
}

/** Player action completed */
export interface ActionMessage extends ServerMessage {
  a: ActionType;
  player: { id: string };
  amount?: number;
  br: number;
}

/** Flop dealt */
export interface FlopMessage extends ServerMessage {
  a: 'flop';
  cards: [string, string, string];
  pot: number;
}

/** Turn dealt */
export interface TurnMessage extends ServerMessage {
  a: 'turn';
  card: string;
  pot: number;
}

/** River dealt */
export interface RiverMessage extends ServerMessage {
  a: 'river';
  card: string;
  pot: number;
}

/** Hand ended */
export interface EndMessage extends ServerMessage {
  a: 'end';
  gn: number;
  pots: Array<{
    id: number;
    value: number;
    players: Array<{ id: string }>;
  }>;
  players: Array<{
    player: { id: string };
    hand: [string, string];
    gain: number;
    br: number;
  }>;
  winners: Array<{
    player: { id: string };
    potId: number;
    gain: number;
    handName: string;
    cards: string[];
  }>;
}

/** Sit-in response */
export interface SitinMessage extends ServerMessage {
  a: 'sitin' | 'sitin2';
  player: { id: string };
  sn: number;
  br: number;
}

/** Player left */
export interface LeaveMessage extends ServerMessage {
  a: 'leave';
  player: { id: string };
  sn: number;
}

/** Chat message */
export interface ChatMessage extends ServerMessage {
  a: 'chat';
  player: { id: string };
  msg: string;
}

/** Ping (keepalive) */
export interface PingMessage extends ServerMessage {
  a: 'ping';
}

/** Hot table response (from manager) */
export interface HotTableMessage extends ServerMessage {
  a: 'hot_table';
  manager: string;
  from: string;
  to: string; // croupier glid or empty string
  status: 'ok' | 'no_place';
}

/** Handoff error response (from manager) */
export interface HandoffMessage extends ServerMessage {
  a: 'handoff';
  from: string;
  manager: string;
  to: string;
  status: string;
}

// =============================================================================
// CLIENT COMMANDS (Outgoing)
// =============================================================================

/** Build client command strings */
export const Commands = {
  // Profile
  getProfile: () => 'getProfile',
  getProfile2: (glid: string) => `getProfile2 ${glid}`,
  setAvatar: (avatarId: number) => `# account avatar ${String(avatarId)}`,
  setNickname: (name: string) => `# account nickname ${name}`,
  setCountry: (code: string) => `# account country ${code}`,
  fetchPublicProfile: (username: string) => `# account public_profile ${username}`,

  // Lobby (Manager commands)
  getHotTable: () => '# gethottable',
  wherePlay: () => '# whereplay',
  wannaJumpIn: (amount = 0, autoRefill = 0, autoRebuy = 0) =>
    amount > 0
      ? `# wannajumpin reserve buyin ${amount} ${autoRefill} ${autoRebuy}`
      : '# wannajumpin reserve buyin',
  wannaPlayMore: (amount = 0, autoRefill = 0, autoRebuy = 0) =>
    amount > 0
      ? `# wannaplaymore reserve buyin ${amount} ${autoRefill} ${autoRebuy}`
      : '# wannaplaymore reserve buyin',

  // Legacy (may not be implemented)
  getGames: () => 'getGames',
  getShop: () => 'getShop',
  getRating: (type: string) => `getRating ${type}`,

  // Table navigation
  wannawatch: (croupier: string) => `# wannawatch ${croupier}`,
  wannaplay: (croupier: string) => `# wannaplay ${croupier}`,

  // Table actions
  watch: () => '# watch',
  sitin: (chips: number, mod1 = 0, mod2 = 0) => `# sitin ${chips} ${mod1} ${mod2}`,
  sitout: () => '# sitout',
  back: () => '# back',
  leave: () => '# leave',
  state: () => '# state',

  // Game actions
  fold: () => '# fold',
  check: () => '# check',
  call: () => '# call',
  raise: (amount: number) => `# raise ${amount}`,
  allin: () => '# allin',

  // Chat
  chat: (msg: string) => `# chat ${msg}`,

  // System
  pong: () => '# pong',
  account: () => '# account',
  buyin: (amount: number) => `# buyin ${amount}`,

  // Jump in: reserve + buyin at croupier (one-step)
  jumpIn: (amount: number, seat?: number, autoRefill = 0, autoRebuy = 0) =>
    seat !== undefined
      ? `# jumpin reserve ${seat} buyin ${amount} ${autoRefill} ${autoRebuy}`
      : `# jumpin reserve buyin ${amount} ${autoRefill} ${autoRebuy}`,

  // Recovery: triggers double_entry detection (seat/amount ignored by server)
  jumpInRecover: () => '# jumpin reserve 1 buyin 100',

  // Gifts (T42: Table Gifts / Throwables)
  // Note: targetSeat is 0-based (client), converted to 1-based (server)
  giftSend: (croupierGlid: string, targetSeat: number, giftId: string) =>
    `# account gift ${croupierGlid} ${String(targetSeat + 1)} ${giftId}`,
};

// =============================================================================
// SERVICE GLIDS
// =============================================================================

/** Well-known service GLIDs */
export const ServiceGLIDs = {
  SYSTEM: '################################',
  MANAGER: 'manager1',
  MANAGER_6MAX: 'manager1',  // 6-max tables
  MANAGER_2MAX: 'manager2',  // 2-max (heads-up) tables
  MANAGER_9MAX: 'manager3',  // 9-max tables
  ACCOUNT: 'account',
  PROFILER: 'profiler',
  ECHO: 'echo',
  INBOX: 'inbox',
} as const;

/**
 * Manager GLID by table size
 * @deprecated Use managers.ts config and autoSelectManager() instead (T37).
 * Kept for backward compatibility with older code paths.
 */
export const ManagerBySize: Record<number, string> = {
  2: ServiceGLIDs.MANAGER_2MAX,
  6: ServiceGLIDs.MANAGER_6MAX,
  9: ServiceGLIDs.MANAGER_9MAX,
};

// =============================================================================
// TYPE GUARDS
// =============================================================================

export function isStartMessage(msg: ServerMessage): msg is StartMessage {
  return msg.a === 'start' || msg.a === 'start2';
}

export function isActMessage(msg: ServerMessage): msg is ActMessage {
  return msg.a === 'act';
}

export function isEndMessage(msg: ServerMessage): msg is EndMessage {
  return msg.a === 'end';
}

export function isActionMessage(msg: ServerMessage): msg is ActionMessage {
  return ['fold', 'check', 'call', 'raise', 'allin'].includes(msg.a);
}
